import React from 'react'
import { Waypoints, MapPin, Plug, Fingerprint, Activity, Check } from 'lucide-react'
import { ScrollReveal } from './ScrollReveal'

const CURRENCIES = ['PHP', 'SGD', 'AUD', 'MYR', 'IDR', 'THB', 'VND', 'NZD']
const CHAIN = ['NS Speed', 'NS Balanced', 'NS Pro']
const BAR_DATA = [40, 65, 50, 80, 60, 90, 75, 55]

export function Features() {
  return (
    <section id="features" className="py-24 bg-[#050810] relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] bg-indigo-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container relative z-10">
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Everything you need to build with AI
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            One API. Multiple models. Built for the Asia-Pacific developer.
          </p>
        </ScrollReveal>

        {/* Bento grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* 1 — Smart Routing (wide) */}
          <ScrollReveal delay={0} className="sm:col-span-2">
            <div className="group relative h-full rounded-2xl border border-yellow-500/20 bg-white/[0.03] hover:bg-white/[0.05] transition-all p-6 overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent" />
              <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-yellow-500/5 rounded-full blur-2xl" />

              <div className="flex items-start justify-between mb-5">
                <div className="inline-flex p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                  <Waypoints className="h-6 w-6 text-yellow-400" />
                </div>
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                  99.9% uptime
                </span>
              </div>

              <h3 className="text-lg font-semibold text-white mb-1">Smart Routing</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-6">
                Automatically routes to the fastest available provider. Fallback chains keep your app alive even when providers go down.
              </p>

              {/* Fallback chain visual */}
              <div className="flex items-center gap-2">
                {CHAIN.map((name, i) => (
                  <React.Fragment key={name}>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-mono ${
                      i === 0
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                        : 'bg-white/5 border-white/10 text-gray-500'
                    }`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${i === 0 ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                      {name}
                    </div>
                    {i < CHAIN.length - 1 && (
                      <span className="text-gray-700 text-xs">→</span>
                    )}
                  </React.Fragment>
                ))}
                <span className="text-xs text-gray-600 ml-1">fallback</span>
              </div>
            </div>
          </ScrollReveal>

          {/* 2 — APAC-First */}
          <ScrollReveal delay={80}>
            <div className="group relative h-full rounded-2xl border border-indigo-500/20 bg-white/[0.03] hover:bg-white/[0.05] transition-all p-6 overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

              <div className="flex items-start justify-between mb-5">
                <div className="inline-flex p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                  <MapPin className="h-6 w-6 text-indigo-400" />
                </div>
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                  6ms avg
                </span>
              </div>

              <h3 className="text-lg font-semibold text-white mb-1">APAC-First</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Edge nodes across SEA. Local currencies, regional compliance, sub-14ms latency.
              </p>

              {/* Currency pills */}
              <div className="flex flex-wrap gap-1.5">
                {CURRENCIES.map(c => (
                  <span key={c} className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* 3 — OpenAI Compatible */}
          <ScrollReveal delay={120}>
            <div className="group relative h-full rounded-2xl border border-sky-500/20 bg-white/[0.03] hover:bg-white/[0.05] transition-all p-6 overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-500/30 to-transparent" />

              <div className="flex items-start justify-between mb-5">
                <div className="inline-flex p-3 rounded-xl bg-sky-500/10 border border-sky-500/20">
                  <Plug className="h-6 w-6 text-sky-400" />
                </div>
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20">
                  Zero migration
                </span>
              </div>

              <h3 className="text-lg font-semibold text-white mb-1">OpenAI Compatible</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Drop-in replacement. Change one line and you're on Neoserve.
              </p>

              <div className="rounded-lg bg-gray-950 border border-white/10 px-4 py-3 font-mono text-xs">
                <span className="text-gray-500">model: </span>
                <span className="text-emerald-400">'ns-speed'</span>
              </div>
            </div>
          </ScrollReveal>

          {/* 4 — Enterprise Security */}
          <ScrollReveal delay={160}>
            <div className="group relative h-full rounded-2xl border border-emerald-500/20 bg-white/[0.03] hover:bg-white/[0.05] transition-all p-6 overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

              <div className="flex items-start justify-between mb-5">
                <div className="inline-flex p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <Fingerprint className="h-6 w-6 text-emerald-400" />
                </div>
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  SOC 2 in progress
                </span>
              </div>

              <h3 className="text-lg font-semibold text-white mb-1">Enterprise Security</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                Multi-tenant isolation, per-key rate limits, and IP whitelisting out of the box.
              </p>

              <ul className="space-y-1.5">
                {['Row Level Security', 'IP Whitelisting', 'Per-key rate limits'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-gray-400">
                    <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          {/* 5 — Usage Analytics (wide) */}
          <ScrollReveal delay={200} className="sm:col-span-1">
            <div className="group relative h-full rounded-2xl border border-violet-500/20 bg-white/[0.03] hover:bg-white/[0.05] transition-all p-6 overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent" />

              <div className="flex items-start justify-between mb-5">
                <div className="inline-flex p-3 rounded-xl bg-violet-500/10 border border-violet-500/20">
                  <Activity className="h-6 w-6 text-violet-400" />
                </div>
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">
                  Real-time
                </span>
              </div>

              <h3 className="text-lg font-semibold text-white mb-1">Usage Analytics</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">
                Cost tracking in your local currency with per-model breakdowns and budget alerts.
              </p>

              {/* Mini bar chart */}
              <div className="flex items-end gap-1 h-10">
                {BAR_DATA.map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-sm bg-violet-500/40 group-hover:bg-violet-500/60 transition-colors"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  )
}
